import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../base-query';
import type {
  CustomPlaybookDto,
  CustomRunDetail,
  CustomRunRecordsPage,
  CustomRunSummary,
  QueuedRunDto,
  SavePlaybookRequest,
  ValidateGraphResponse,
  WebhookTestResult,
  CodeCompileResult,
  PlayHoldsSummary,
  PlayHoldsPage,
  PlayHoldKind,
  SampleEstimate,
  TriggerConfig,
  TriggerMatches,
  TryApprovalRequest,
} from '@/types/custom-playbooks';

/**
 * Custom Playbook Builder API — wire contract for the backend router at
 * cleanse-api-v2 `cleanse_api/api/leads/custom_playbooks.py`
 * (prefix /api/v1/custom-playbooks).
 *
 * All bodies are 1:1 camelCase with the Pydantic DTOs (architecture §2.2/§12).
 */

export const advancedPlaybooksApi = createApi({
  reducerPath: 'advancedPlaybooksApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['CustomPlaybook', 'CustomRun', 'SyncMapping', 'AgentSettings'],
  endpoints: (builder) => ({
    looksGood: builder.mutation<
      CustomRunSummary,
      { playbookId: string; organizationId: string; body: TryApprovalRequest }
    >({
      query: ({ playbookId, organizationId, body }) => ({
        url: `/custom-playbooks/${playbookId}/looks-good`,
        method: 'POST',
        body,
        headers: { 'X-Cleanlist-Expected-Org': organizationId },
      }),
      invalidatesTags: (_result, _error, { playbookId, body }) => [
        { type: 'CustomPlaybook', id: playbookId },
        { type: 'CustomRun', id: body.tryId },
        { type: 'CustomRun', id: 'LIST' },
      ],
    }),
    getPlaySampleEstimate: builder.query<
      SampleEstimate,
      {
        playbookId: string;
        organizationId: string;
        /** Off-flag Sample 5 records, or 1 from a picked record; Try it is 5. */
        records?: number;
      }
    >({
      query: ({ playbookId, organizationId, records = 5 }) => ({
        url: `/custom-playbooks/${playbookId}/sample-estimate`,
        params: { records },
        headers: { 'X-Cleanlist-Expected-Org': organizationId },
      }),
    }),
    runPlaySample: builder.mutation<
      CustomRunSummary,
      {
        playbookId: string;
        organizationId: string;
        /** Off-flag Sample 5 records, or 1 from a picked record; Try it sends none. */
        records?: number;
        confirmCredits: number;
        /** Try it (try_it_v1): the reviewed quote. */
        quoteId?: string;
        /** W2-56, off-flag only: the one picked record this sample starts from. */
        testRecord?: { id: string };
      }
    >({
      query: ({
        playbookId,
        organizationId,
        records,
        confirmCredits,
        quoteId,
        testRecord,
      }) => ({
        url: `/custom-playbooks/${playbookId}/run-sample`,
        method: 'POST',
        body: {
          ...(records !== undefined ? { records } : {}),
          confirmCredits,
          ...(quoteId ? { quoteId } : {}),
          ...(testRecord ? { testRecord } : {}),
        },
        headers: { 'X-Cleanlist-Expected-Org': organizationId },
      }),
      invalidatesTags: [{ type: 'CustomRun', id: 'LIST' }],
    }),
    // W1: how many CRM records the trigger on screen (saved or not) matches
    // now, read through the agent's own CRM connection.
    countPlayTriggerMatches: builder.mutation<
      TriggerMatches,
      { organizationId: string; playbookId: string; trigger: TriggerConfig }
    >({
      query: ({ organizationId, playbookId, trigger }) => ({
        url: `/custom-playbooks/${playbookId}/trigger-matches`,
        method: 'POST',
        body: { trigger },
        headers: { 'X-Cleanlist-Expected-Org': organizationId },
      }),
    }),
    compileCode: builder.mutation<
      CodeCompileResult,
      { orgId: string; playbookId: string; nodeId: string; source: string }
    >({
      query: ({ orgId, playbookId, nodeId, source }) => ({
        url: `/custom-playbooks/${playbookId}/nodes/${encodeURIComponent(nodeId)}/code-compile`,
        method: 'POST',
        body: { source },
        headers: { 'X-Cleanlist-Expected-Org': orgId },
      }),
    }),
    getPlayHoldsSummary: builder.query<
      PlayHoldsSummary,
      { organizationId: string; playbookId: string }
    >({
      query: ({ organizationId, playbookId }) => ({
        url: `/custom-playbooks/${playbookId}/holds/summary`,
        method: 'GET',
        headers: { 'X-Cleanlist-Expected-Org': organizationId },
      }),
      providesTags: (_r, _e, { playbookId }) => [
        { type: 'CustomPlaybook', id: playbookId },
      ],
    }),
    getPlayHolds: builder.query<
      PlayHoldsPage,
      {
        organizationId: string;
        playbookId: string;
        kind?: PlayHoldKind;
        nodeId?: string;
        cursor?: string;
      }
    >({
      query: ({ organizationId, playbookId, ...params }) => ({
        url: `/custom-playbooks/${playbookId}/holds`,
        method: 'GET',
        params,
        headers: { 'X-Cleanlist-Expected-Org': organizationId },
      }),
    }),
    sendWebhookTest: builder.mutation<
      WebhookTestResult,
      { orgId: string; playbookId: string; nodeId: string }
    >({
      query: ({ orgId, playbookId, nodeId }) => ({
        url: `/custom-playbooks/${playbookId}/nodes/${encodeURIComponent(nodeId)}/webhook-test`,
        method: 'POST',
        headers: { 'X-Cleanlist-Expected-Org': orgId },
      }),
    }),
    listCustomPlaybooks: builder.query<
      CustomPlaybookDto[],
      void | { organizationId: string }
    >({
      query: (scope) => ({
        url: '/custom-playbooks',
        method: 'GET',
        ...(scope
          ? { headers: { 'X-Cleanlist-Expected-Org': scope.organizationId } }
          : {}),
      }),
      providesTags: (result) => [
        { type: 'CustomPlaybook' as const, id: 'LIST' },
        ...(result ?? []).map((p) => ({
          type: 'CustomPlaybook' as const,
          id: p.id,
        })),
      ],
    }),
    getCustomPlaybook: builder.query<
      CustomPlaybookDto,
      string | { id: string; organizationId: string }
    >({
      query: (scope) => ({
        url: `/custom-playbooks/${typeof scope === 'string' ? scope : scope.id}`,
        method: 'GET',
        ...(typeof scope === 'string'
          ? {}
          : {
              headers: { 'X-Cleanlist-Expected-Org': scope.organizationId },
            }),
      }),
      providesTags: (_r, _e, scope) => [
        {
          type: 'CustomPlaybook',
          id: typeof scope === 'string' ? scope : scope.id,
        },
      ],
    }),
    createCustomPlaybook: builder.mutation<CustomPlaybookDto, { name: string }>(
      {
        query: (body) => ({ url: '/custom-playbooks', method: 'POST', body }),
        invalidatesTags: [{ type: 'CustomPlaybook', id: 'LIST' }],
      }
    ),
    // Deep-copies a playbook's graph into a fresh disabled draft ("<name> (copy)"),
    // with its own destination list. Returns the new playbook.
    duplicateCustomPlaybook: builder.mutation<CustomPlaybookDto, string>({
      query: (id) => ({
        url: `/custom-playbooks/${id}/duplicate`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'CustomPlaybook', id: 'LIST' }],
    }),
    saveCustomPlaybook: builder.mutation<
      CustomPlaybookDto,
      { id: string; body: SavePlaybookRequest; organizationId?: string }
    >({
      query: ({ id, body, organizationId }) => ({
        url: `/custom-playbooks/${id}`,
        method: 'PUT',
        body,
        ...(organizationId
          ? { headers: { 'X-Cleanlist-Expected-Org': organizationId } }
          : {}),
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'CustomPlaybook', id },
        { type: 'CustomPlaybook', id: 'LIST' },
      ],
    }),
    // A dry validation of a graph (nothing saved). Read as a query so the
    // canvas can follow its warnings, such as a Sync step whose default field
    // mapping leaves out what the play enriches (`crm_sync_unmapped_fields`);
    // a change to the org's field mapping reads it again, and so
    // does a saved change to an agent's switches or apps (`patchAgent`).
    validateAdvancedGraph: builder.query<
      ValidateGraphResponse,
      {
        id: string;
        body: SavePlaybookRequest;
        organizationId?: string;
        /** Part of the cache key only: an agent's settings changing asks again. */
        agentSettings?: string;
      }
    >({
      query: ({ id, body, organizationId }) => ({
        ...(organizationId
          ? { headers: { 'X-Cleanlist-Expected-Org': organizationId } }
          : {}),
        url: `/custom-playbooks/${id}/validate`,
        method: 'POST',
        body,
      }),
      providesTags: ['SyncMapping', 'AgentSettings'],
    }),
    deleteCustomPlaybook: builder.mutation<void, string>({
      query: (id) => ({ url: `/custom-playbooks/${id}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'CustomPlaybook', id },
        { type: 'CustomPlaybook', id: 'LIST' },
      ],
    }),
    // Run now = start ONE live run on demand (the manual counterpart of the
    // poll). This DOES spend credits and write to the CRM/list.
    // No body — the trigger config is the seed source, sourced live server-side.
    // A cleanup play without a recent scan queues instead (202, W5 3.3).
    runCustomPlaybookNow: builder.mutation<
      CustomRunSummary | QueuedRunDto,
      string
    >({
      query: (id) => ({
        url: `/custom-playbooks/${id}/run`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'CustomRun', id: 'LIST' }],
    }),
    cancelAdvancedRun: builder.mutation<void, string>({
      query: (runId) => ({
        url: `/custom-playbooks/runs/${runId}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, runId) => [{ type: 'CustomRun', id: runId }],
    }),
    listAdvancedRuns: builder.query<
      CustomRunSummary[],
      string | { playbookId: string; organizationId: string }
    >({
      query: (scope) => ({
        url: `/custom-playbooks/${typeof scope === 'string' ? scope : scope.playbookId}/runs`,
        ...(typeof scope === 'string'
          ? {}
          : { headers: { 'X-Cleanlist-Expected-Org': scope.organizationId } }),
        method: 'GET',
      }),
      providesTags: (result) => [
        { type: 'CustomRun' as const, id: 'LIST' },
        ...(result ?? []).map((r) => ({
          type: 'CustomRun' as const,
          id: r.id,
        })),
      ],
    }),
    // Poll while live: callers pass `pollingInterval: 3000` (0 once terminal) —
    // see run-overlay.hook.ts. The 3s poll is the reconciliation floor.
    getAdvancedRun: builder.query<
      CustomRunDetail,
      string | { runId: string; organizationId: string }
    >({
      query: (scope) => ({
        url: `/custom-playbooks/runs/${typeof scope === 'string' ? scope : scope.runId}`,
        method: 'GET',
        ...(typeof scope === 'string'
          ? {}
          : {
              headers: { 'X-Cleanlist-Expected-Org': scope.organizationId },
            }),
      }),
      providesTags: (_r, _e, scope) => [
        {
          type: 'CustomRun',
          id: typeof scope === 'string' ? scope : scope.runId,
        },
      ],
    }),
    getAdvancedRunRecords: builder.query<
      CustomRunRecordsPage,
      {
        runId: string;
        organizationId?: string;
        nodeId?: string;
        status?: string;
        cursor?: string;
        limit?: number;
        include?: 'outputs';
        /** Separates the reviewed payload's cache; never sent to the server. */
        previewRevision?: string | null;
        /** One record of the run, e.g. to read its path on demand. */
        recordId?: string;
        /** Only the trigger's items: records with no parent in the run. */
        roots?: boolean;
        /** Only one record's children (a company's people from Find People). */
        parentId?: string;
      }
    >({
      query: ({
        runId,
        organizationId,
        nodeId,
        status,
        cursor,
        limit,
        include,
        recordId,
        roots,
        parentId,
      }) => ({
        url: `/custom-playbooks/runs/${runId}/records`,
        method: 'GET',
        ...(organizationId
          ? { headers: { 'X-Cleanlist-Expected-Org': organizationId } }
          : {}),
        params: {
          ...(nodeId ? { node_id: nodeId } : {}),
          ...(status ? { status } : {}),
          ...(cursor ? { cursor } : {}),
          ...(limit ? { limit } : {}),
          ...(include ? { include } : {}),
          ...(recordId ? { record_id: recordId } : {}),
          ...(roots ? { roots: true } : {}),
          ...(parentId ? { parent_id: parentId } : {}),
        },
      }),
    }),
  }),
});

export const {
  useLooksGoodMutation,
  useLazyGetAdvancedRunQuery,
  useLazyGetPlaySampleEstimateQuery,
  useRunPlaySampleMutation,
  useCountPlayTriggerMatchesMutation,
  useGetPlayHoldsSummaryQuery,
  useLazyGetPlayHoldsSummaryQuery,
  useGetPlayHoldsQuery,
  useSendWebhookTestMutation,
  useCompileCodeMutation,
  useListCustomPlaybooksQuery,
  useGetCustomPlaybookQuery,
  useCreateCustomPlaybookMutation,
  useDuplicateCustomPlaybookMutation,
  useSaveCustomPlaybookMutation,
  useValidateAdvancedGraphQuery,
  useDeleteCustomPlaybookMutation,
  useRunCustomPlaybookNowMutation,
  useCancelAdvancedRunMutation,
  useListAdvancedRunsQuery,
  useGetAdvancedRunQuery,
  useGetAdvancedRunRecordsQuery,
} = advancedPlaybooksApi;
